import { Capacitor } from '@capacitor/core';
import { openExternalUrl, initNativeUrlOpenListener } from './externalUrl.js';
import { rememberSignupSite, clearAccountMarket } from './market.js';

// Cognito's sign-up page can't get back to the code screen once it's closed
// (signing in then just says "User is not confirmed"). Remember that sign-up
// was started on this device so the app can point a signed-out person who
// didn't finish to confirm.html. Cleared on sign-in, on confirming there, or
// when they dismiss the message. Same key is cleared by confirm.html.
const SIGNUP_STARTED_KEY = 'zephyr_signup_started';
const SIGNUP_REMINDER_DAYS = 14;

function markSignupStarted() {
    try { localStorage.setItem(SIGNUP_STARTED_KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
}

export function signupUnfinished() {
    try {
        const at = Number(localStorage.getItem(SIGNUP_STARTED_KEY));
        return at > 0 && Date.now() - at < SIGNUP_REMINDER_DAYS * 24 * 60 * 60 * 1000;
    } catch (e) { return false; }
}

// Android app: native navigation keeps its own copy of the session
// (ZephyrAuthStore) so it can renew the 60-minute access token itself -- on
// long drives and for trips started from Android Auto, where there's no web
// page. App versions without setAuthSession just skip this.
function syncNativeSession(tokens) {
    try {
        const plugin = window.Capacitor?.Plugins?.ZephyrNavigation;
        if (tokens) {
            plugin?.setAuthSession?.({
                refreshToken: tokens.refresh_token || null,
                accessToken: tokens.access_token || null,
                expiresAt: tokens.expires_at || 0
            })?.catch?.(() => {});
        } else {
            plugin?.clearAuthSession?.()?.catch?.(() => {});
        }
    } catch (e) { /* not the Android app, or an older one */ }
}

export function clearSignupStarted() {
    try { localStorage.removeItem(SIGNUP_STARTED_KEY); } catch (e) { /* storage blocked */ }
}

/**
 * AuthService - Cognito authentication using the hosted UI + PKCE flow
 * No external libraries needed — uses native browser APIs
 *
 * Native platform note: Google blocks OAuth sign-in inside an embedded
 * WebView (Capacitor's default) as an anti-phishing policy -- confirmed via
 * a real 400 "malformed request" error at Google's own consent step once
 * the WebView was allowed to navigate there directly. See externalUrl.js
 * for the shared fix (Chrome Custom Tabs + the zephyr.travel App Link) --
 * also used by SubscriptionService for the exact same reason on Stripe
 * checkout.
 */
export class AuthService {
    constructor() {
        this.userPoolId = 'us-west-1_60ZljlECc';
        this.clientId = '5nanfcl5ptsda54r34mnms0fkm';
        this.region = 'us-west-1';
        this.redirectUri = window.location.origin + '/app.html';
        this.cognitoDomain = `https://us-west-160zljlecc.auth.us-west-1.amazoncognito.com`;
        this.tokenKey = 'zephyr_auth_tokens';
        this.userKey = 'zephyr_user';

        initNativeUrlOpenListener();
        // Already signed in from before this app version: hand over the session.
        try {
            const saved = JSON.parse(localStorage.getItem(this.tokenKey) || 'null');
            if (saved?.refresh_token) syncNativeSession(saved);
        } catch (e) { /* storage blocked or unreadable */ }
    }

    /**
     * Redirect to Cognito hosted login page
     */
    async signIn() {
        rememberSignupSite();
        const state = this._randomString(32);
        const codeVerifier = this._randomString(64);
        const codeChallenge = await this._generateCodeChallenge(codeVerifier);

        // Store for use after redirect
        localStorage.setItem('pkce_state', state);
        localStorage.setItem('pkce_verifier', codeVerifier);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            scope: 'openid email profile',
            state,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256'
        });

        await openExternalUrl(`${this.cognitoDomain}/oauth2/authorize?${params}`);
    }

    /**
     * Redirect straight to Google's own consent screen, skipping Cognito's
     * hosted-UI chooser page -- same PKCE flow as signIn(), just with
     * identity_provider=Google added so returning OR new Google users land
     * on Google immediately (the "one-tap" option in the sign-in prompt).
     */
    async signInWithGoogle() {
        rememberSignupSite();
        const state = this._randomString(32);
        const codeVerifier = this._randomString(64);
        const codeChallenge = await this._generateCodeChallenge(codeVerifier);

        localStorage.setItem('pkce_state', state);
        localStorage.setItem('pkce_verifier', codeVerifier);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            scope: 'openid email profile',
            state,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            identity_provider: 'Google'
        });

        await openExternalUrl(`${this.cognitoDomain}/oauth2/authorize?${params}`);
    }

    /**
     * Redirect to Cognito hosted signup page
     */
    async signUp() {
        rememberSignupSite();
        markSignupStarted();
        const state = this._randomString(32);
        const codeVerifier = this._randomString(64);
        const codeChallenge = await this._generateCodeChallenge(codeVerifier);

        // Store for use after redirect
        localStorage.setItem('pkce_state', state);
        localStorage.setItem('pkce_verifier', codeVerifier);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            scope: 'openid email profile',
            state,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256'
        });

        await openExternalUrl(`${this.cognitoDomain}/signup?${params}`);
    }

    /**
     * Sign out from both the app and Cognito
     */
    signOut() {
        // Clear auth tokens
        localStorage.removeItem(this.tokenKey);
        syncNativeSession(null);
        localStorage.removeItem(this.userKey);
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_verifier');
        clearAccountMarket();

        // Clear profile cache (Bug #6 fix)
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('zephyr_profile_')) {
                localStorage.removeItem(key);
            }
        });

        // Sign out from Cognito to fully clear the session
        const params = new URLSearchParams({
            client_id: this.clientId,
            logout_uri: window.location.origin + '/app.html'
        });
        const logoutUrl = `${this.cognitoDomain}/logout?${params}`;

        // Android app: the Cognito session lives in Chrome (sign-in runs in a
        // Custom Tab, see externalUrl.js), not in the app's WebView, so the
        // logout has to run there too -- otherwise the next Sign In skips the
        // password and Sign Up says "You're still signed in". Chrome's return
        // to /app.html comes back as an App Link and just closes the tab
        // (initNativeUrlOpenListener). The app reloads its own pinned page
        // (beta/vN/app.html), not the website's /app.html.
        if (Capacitor.isNativePlatform()) {
            openExternalUrl(logoutUrl)
                .catch(() => {})
                .finally(() => { window.location.href = window.location.pathname; });
            return;
        }

        window.location.href = logoutUrl;
    }

    /**
     * Handle the OAuth callback — call this on app load if ?code= is in URL
     */
    async handleCallback() {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (!code) return false;

        const storedState = localStorage.getItem('pkce_state');
        const codeVerifier = localStorage.getItem('pkce_verifier');

        if (state !== storedState) {
            console.error('State mismatch — possible CSRF attack');
            return false;
        }

        try {
            // Exchange code for tokens
            const response = await fetch(`${this.cognitoDomain}/oauth2/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    grant_type: 'authorization_code',
                    client_id: this.clientId,
                    redirect_uri: this.redirectUri,
                    code,
                    code_verifier: codeVerifier
                })
            });

            const tokens = await response.json();

            if (tokens.error) {
                console.error('Token exchange error:', tokens.error);
                return false;
            }

            // Store tokens
            const stored = { ...tokens, expires_at: Date.now() + (tokens.expires_in * 1000) };
            localStorage.setItem(this.tokenKey, JSON.stringify(stored));
            syncNativeSession(stored);

            // Decode and store user info from ID token
            const user = this._decodeJwt(tokens.id_token);
            localStorage.setItem(this.userKey, JSON.stringify({
                email: user.email,
                sub: user.sub,
                name: user.name || user.email
            }));

            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            localStorage.removeItem('pkce_state');
            localStorage.removeItem('pkce_verifier');

            return true;
        } catch (error) {
            console.error('Auth callback error:', error);
            return false;
        }
    }

    /**
     * Returns true if user is signed in with a valid token
     * Checks expiry but doesn't auto-refresh (call refreshIfNeeded for that)
     */
    isSignedIn() {
        const tokens = this._getTokens();
        if (!tokens) return false;
        return tokens.expires_at > Date.now();
    }

    /**
     * Refresh the access token silently if it's expired or about to expire
     * Returns true if still signed in after refresh attempt
     */
    async refreshIfNeeded() {
        const tokens = this._getTokens();
        if (!tokens) return false;

        // Refresh if token expires within 5 minutes
        const fiveMinutes = 5 * 60 * 1000;
        if (tokens.expires_at > Date.now() + fiveMinutes) {
            return true; // Still valid, no refresh needed
        }

        if (!tokens.refresh_token) return false;

        try {
            const response = await fetch(`${this.cognitoDomain}/oauth2/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    grant_type: 'refresh_token',
                    client_id: this.clientId,
                    refresh_token: tokens.refresh_token
                })
            });

            const newTokens = await response.json();

            if (newTokens.error) {
                // Refresh token expired — clear session
                localStorage.removeItem(this.tokenKey);
                localStorage.removeItem(this.userKey);
                syncNativeSession(null);
                return false;
            }

            // Save new tokens (keep existing refresh_token if not returned)
            const refreshed = {
                ...newTokens,
                refresh_token: newTokens.refresh_token || tokens.refresh_token,
                expires_at: Date.now() + (newTokens.expires_in * 1000)
            };
            localStorage.setItem(this.tokenKey, JSON.stringify(refreshed));
            syncNativeSession(refreshed);

            console.log('✅ Token refreshed silently');
            return true;
        } catch (error) {
            console.error('Token refresh error:', error);
            return false;
        }
    }

    /**
     * Returns the current user object or null
     */
    getUser() {
        try {
            const raw = localStorage.getItem(this.userKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    /**
     * Returns the access token for API calls
     */
    getAccessToken() {
        const tokens = this._getTokens();
        return tokens ? tokens.access_token : null;
    }

    // ── Private helpers ──────────────────────────────────────────────────────

    _getTokens() {
        try {
            const raw = localStorage.getItem(this.tokenKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    _randomString(length) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        return Array.from(array).map(b => chars[b % chars.length]).join('');
    }

    async _generateCodeChallenge(verifier) {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await crypto.subtle.digest('SHA-256', data);
        return btoa(String.fromCharCode(...new Uint8Array(digest)))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    _decodeJwt(token) {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    }
}
